import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

import { Session } from '@supabase/supabase-js';

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    signInWithMagicLink: (email: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);

    console.log('[AuthProvider] Render - user:', !!user, 'loading:', loading);

    // Safety timeout: Forçar loading=false após 5s se algo travar
    useEffect(() => {
        const timer = setTimeout(() => {
            setLoading((prev) => {
                if (prev) {
                    console.warn('[AuthContext] ⚠️ Timeout de segurança atingido. Forçando loading=false.');
                    return false;
                }
                return prev;
            });
        }, 5000);
        return () => clearTimeout(timer);
    }, []);

    // Use refs to track values and avoid stale closure in the event listener
    const userIdRef = React.useRef<string | undefined>(undefined);
    const sessionRef = React.useRef<Session | null>(null);

    // Update sessionRef whenever session changes
    React.useEffect(() => {
        sessionRef.current = session;
    }, [session]);

    useEffect(() => {
        let isInitialized = false;

        // CRITICAL: Register listener FIRST before any async operations
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            console.log('[AuthContext] ========================================');
            console.log('[AuthContext] Auth state change:', event);
            console.log('[AuthContext] New session exists:', !!newSession);
            console.log('[AuthContext] New session user:', newSession?.user?.email);
            console.log('[AuthContext] Event timestamp:', new Date().toISOString());

            if (event === 'INITIAL_SESSION') {
                console.log('[AuthContext] INITIAL_SESSION event received');
                if (!newSession) {
                    console.warn('[AuthContext] ⚠️ INITIAL_SESSION recebido SEM sessão. O usuário pode ter sido desconectado ou o storage falhou.');
                }
                // Set loading to false on INITIAL_SESSION regardless of session state
                console.log('[AuthContext] Setting loading to FALSE');
                setLoading(false);
                console.log('[AuthContext] setLoading(false) called - component should re-render now');
                isInitialized = true;
            }

            // Use ref para comparação para evitar stale closure
            const currentSession = sessionRef.current;

            // CRITICAL: Log SIGNED_OUT events to debug unexpected logouts
            if (event === 'SIGNED_OUT') {
                console.error('[AuthContext] 🚨 SIGNED_OUT EVENT DETECTED!');
                console.error('[AuthContext] Stack trace:', new Error().stack);
                console.error('[AuthContext] Current URL:', window.location.href);
                console.error('[AuthContext] Previous session:', currentSession?.user?.email);
                console.error('[AuthContext] Timestamp:', new Date().toISOString());
            }

            // Atualiza a sessão se o token mudou (para manter headers atualizados)
            if (newSession?.access_token !== currentSession?.access_token) {
                console.log('[AuthContext] Token atualizado');
                console.log('[AuthContext] Old token:', currentSession?.access_token?.substring(0, 20) + '...');
                console.log('[AuthContext] New token:', newSession?.access_token?.substring(0, 20) + '...');
                setSession(newSession);
                sessionRef.current = newSession;

                // Salvar sessão no localStorage para api.ts usar
                if (newSession) {
                    localStorage.setItem('sb-driver-financas-auth-token', JSON.stringify(newSession));
                    console.log('[AuthContext] Sessão salva no localStorage');
                } else {
                    localStorage.removeItem('sb-driver-financas-auth-token');
                    console.log('[AuthContext] Sessão removida do localStorage');
                }
            }

            // CRÍTICO: Atualiza o objeto USER
            // Para INITIAL_SESSION, sempre atualiza independente do ID
            // Para outros eventos, só atualiza se o ID mudar (previne reloads desnecessários)
            console.log('[AuthContext] Checking if should update user...');
            console.log('[AuthContext] - event:', event);
            console.log('[AuthContext] - newSession?.user?.id:', newSession?.user?.id);
            console.log('[AuthContext] - userIdRef.current:', userIdRef.current);
            console.log('[AuthContext] - newSession?.user:', newSession?.user);

            const shouldUpdateUser = event === 'INITIAL_SESSION' || (newSession?.user?.id !== userIdRef.current);
            console.log('[AuthContext] - shouldUpdateUser:', shouldUpdateUser);

            if (shouldUpdateUser) {
                console.log('[AuthContext] ✅ Atualizando usuário');
                console.log('[AuthContext] Event:', event);
                console.log('[AuthContext] Old user ID:', userIdRef.current);
                console.log('[AuthContext] New user ID:', newSession?.user?.id);
                console.log('[AuthContext] New user email:', newSession?.user?.email);
                setUser(newSession?.user ?? null);
                userIdRef.current = newSession?.user?.id;
            } else {
                console.log('[AuthContext] ❌ Skipping user update');
            }

            // Verificar se o token está prestes a expirar e tentar refresh preventivo
            if (newSession && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
                const now = Math.floor(Date.now() / 1000);
                const expiresAt = newSession.expires_at;

                if (expiresAt) {
                    const timeUntilExpiry = expiresAt - now;
                    console.log('[AuthContext] Token expira em:', timeUntilExpiry, 'segundos');
                }

                // Aumentar o threshold para 15 minutos (900 segundos) para evitar race conditions
                if (expiresAt && (expiresAt - now) < 900) {
                    console.log('[AuthContext] Token expira em breve, fazendo refresh preventivo');
                    try {
                        const { data: refreshData, error } = await supabase.auth.refreshSession();
                        if (!error && refreshData.session) {
                            console.log('[AuthContext] Refresh preventivo realizado com sucesso');
                            setSession(refreshData.session);
                            sessionRef.current = refreshData.session;
                            localStorage.setItem('sb-driver-financas-auth-token', JSON.stringify(refreshData.session));
                        } else {
                            console.warn('[AuthContext] Falha no refresh preventivo:', error);
                            // Se o refresh falhar, tentar novamente em 5 minutos
                            setTimeout(() => {
                                console.log('[AuthContext] Tentando refresh novamente...');
                                supabase.auth.refreshSession().then(({ data, error }) => {
                                    if (!error && data.session) {
                                        setSession(data.session);
                                        sessionRef.current = data.session;
                                        localStorage.setItem('sb-driver-financas-auth-token', JSON.stringify(data.session));
                                    }
                                });
                            }, 300000); // 5 minutos
                        }
                    } catch (refreshError) {
                        console.warn('[AuthContext] Falha no refresh preventivo:', refreshError);
                        // Agendar nova tentativa em caso de erro
                        setTimeout(() => {
                            console.log('[AuthContext] Tentando refresh após erro...');
                            supabase.auth.refreshSession().then(({ data, error }) => {
                                if (!error && data.session) {
                                    setSession(data.session);
                                    sessionRef.current = data.session;
                                    localStorage.setItem('sb-driver-financas-auth-token', JSON.stringify(data.session));
                                }
                            });
                        }, 60000); // 1 minuto
                    }
                }
            }

            // Only set loading to false if not already done by INITIAL_SESSION
            if (event !== 'INITIAL_SESSION') {
                setLoading(false);
            }
            console.log('[AuthContext] ========================================');
        });

        return () => subscription.unsubscribe();
    }, []);

    const signInWithMagicLink = async (email: string) => {
        // Usar Supabase diretamente para enviar magic link com redirect correto
        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                // CRÍTICO: Redirecionar para /auth/callback, não para /login
                // Isso garante que novos usuários sejam processados corretamente
                // e redirecionados para /onboarding
                emailRedirectTo: `${window.location.origin}/auth/callback`
            }
        });

        if (error) {
            console.error('[AuthContext] Erro ao enviar magic link:', error);
            throw new Error('Erro ao enviar link de login');
        }

        console.log('[AuthContext] Magic link enviado com sucesso para:', email);
        console.log('[AuthContext] Redirect URL:', `${window.location.origin}/auth/callback`);
    };

    const signInWithGoogle = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.hostname === 'localhost'
                    ? `${window.location.origin}/dashboard`
                    : `${window.location.origin}/dashboard`
            }
        });

        if (error) {
            console.error('Google OAuth error:', error);
            throw new Error('Erro ao fazer login com Google');
        }
    };

    const signOut = async () => {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
    };

    const value = React.useMemo(() => ({
        user,
        session,
        loading,
        signInWithMagicLink,
        signInWithGoogle,
        signOut
    }), [user, session, loading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
